// =============================================================================
// BP-05-03: Calendar Frontend - AppointmentForm Component
// =============================================================================

import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format, addHours, parseISO } from 'date-fns';
import {
  Calendar,
  Clock,
  MapPin,
  Video,
  User,
  Repeat,
  Bell,
  FileText,
  HelpCircle,
  X,
  Plus,
  Trash2,
  AlertTriangle,
} from 'lucide-react';
import {
  type AppointmentFormData,
  type Appointment,
  AppointmentType,
  LocationType,
  RecurrenceFrequency,
  APPOINTMENT_TYPE_CONFIG,
  LOCATION_TYPE_CONFIG,
  APPOINTMENT_COLORS,
  REMINDER_PRESETS,
} from '../types';
import { RecurrenceEditor } from './RecurrenceEditor';
import { ReminderSettings } from './ReminderSettings';
import { ConflictWarning } from './ConflictWarning';
import { useConflictCheck, useCreateAppointment, useUpdateAppointment } from '../hooks';
import { useCalendarStore } from '../stores/calendarStore';
import { cn } from '@/core/lib/utils';

// -----------------------------------------------------------------------------
// Validation Schema
// -----------------------------------------------------------------------------

const appointmentSchema = z.object({
  title: z.string().min(1, 'Title is required').max(300),
  description: z.string().max(2000).optional(),
  appointmentType: z.nativeEnum(AppointmentType),
  childId: z.string().optional(),
  providerName: z.string().max(200).optional(),
  providerId: z.string().optional(),
  locationType: z.nativeEnum(LocationType),
  locationName: z.string().max(200).optional(),
  locationAddress: z.string().max(500).optional(),
  telehealthLink: z.string().url().optional().or(z.literal('')),
  startsAt: z.string().min(1, 'Start time is required'),
  endsAt: z.string().min(1, 'End time is required'),
  timezone: z.string(),
  isAllDay: z.boolean(),
  isRecurring: z.boolean(),
  reminderMinutes: z.array(z.number()),
  notes: z.string().max(5000).optional(),
  color: z.string().optional(),
});

type FormData = z.infer<typeof appointmentSchema>;

// -----------------------------------------------------------------------------
// Props
// -----------------------------------------------------------------------------

interface AppointmentFormProps {
  familyId: string;
  appointment?: Appointment;
  initialStartDate?: Date;
  initialEndDate?: Date;
  children?: Array<{ id: string; name: string }>;
  careTeam?: Array<{ id: string; name: string; type: string }>;
  onSuccess?: (appointment: Appointment) => void;
  onCancel?: () => void;
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function AppointmentForm({
  familyId,
  appointment,
  initialStartDate,
  initialEndDate,
  children = [],
  careTeam = [],
  onSuccess,
  onCancel,
}: AppointmentFormProps) {
  const { defaultDuration, timeFormat, quickAddSlot, closeForm } = useCalendarStore();
  const createMutation = useCreateAppointment();
  const updateMutation = useUpdateAppointment();

  const isEditing = !!appointment;

  // Calculate initial dates
  const getInitialDates = () => {
    if (appointment) {
      return {
        startsAt: appointment.startsAt,
        endsAt: appointment.endsAt,
      };
    }
    if (quickAddSlot) {
      return {
        startsAt: quickAddSlot.start.toISOString(),
        endsAt: quickAddSlot.end.toISOString(),
      };
    }
    if (initialStartDate) {
      const endDate = initialEndDate || addHours(initialStartDate, 1);
      return {
        startsAt: initialStartDate.toISOString(),
        endsAt: endDate.toISOString(),
      };
    }
    const now = new Date();
    now.setMinutes(Math.ceil(now.getMinutes() / 30) * 30, 0, 0);
    return {
      startsAt: now.toISOString(),
      endsAt: addHours(now, 1).toISOString(),
    };
  };

  const initialDates = getInitialDates();

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(appointmentSchema),
    defaultValues: {
      title: appointment?.title || '',
      description: appointment?.description || '',
      appointmentType: appointment?.appointmentType || AppointmentType.MEDICAL,
      childId: appointment?.childId || '',
      providerName: appointment?.providerName || '',
      providerId: appointment?.providerId || '',
      locationType: appointment?.locationType || LocationType.IN_PERSON,
      locationName: appointment?.locationName || '',
      locationAddress: appointment?.locationAddress || '',
      telehealthLink: appointment?.telehealthLink || '',
      startsAt: initialDates.startsAt,
      endsAt: initialDates.endsAt,
      timezone: appointment?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      isAllDay: appointment?.isAllDay || false,
      isRecurring: appointment?.isRecurring || false,
      reminderMinutes: appointment?.reminderMinutes || [60, 1440],
      notes: appointment?.notes || '',
      color: appointment?.color || APPOINTMENT_COLORS[0].value,
    },
  });

  const [showRecurrence, setShowRecurrence] = useState(false);
  const [recurrenceRule, setRecurrenceRule] = useState(appointment?.recurrenceRule || '');
  const [prepChecklist, setPrepChecklist] = useState(appointment?.prepChecklist || []);
  const [questions, setQuestions] = useState(appointment?.questionsToAsk || []);
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [newQuestion, setNewQuestion] = useState('');

  const watchStartsAt = watch('startsAt');
  const watchEndsAt = watch('endsAt');
  const watchLocationType = watch('locationType');
  const watchIsAllDay = watch('isAllDay');
  const watchIsRecurring = watch('isRecurring');

  // Conflict check
  const { data: conflictData, isLoading: isCheckingConflicts } = useConflictCheck(
    familyId,
    watchStartsAt,
    watchEndsAt,
    appointment?.id,
    { enabled: !!watchStartsAt && !!watchEndsAt }
  );

  // Handle form submission
  const onSubmit = async (data: FormData) => {
    const formData: AppointmentFormData = {
      ...data,
      prepChecklist: prepChecklist.map((item) => ({
        item: item.item,
        completed: item.completed,
      })),
      questionsToAsk: questions.map((q) => ({ question: q.question })),
      recurrence: watchIsRecurring && recurrenceRule ? parseRecurrenceRule(recurrenceRule) : undefined,
    };

    try {
      let result: Appointment;
      if (isEditing) {
        result = await updateMutation.mutateAsync({
          appointmentId: appointment.id,
          data: formData,
        });
      } else {
        result = await createMutation.mutateAsync({
          familyId,
          data: formData,
        });
      }
      onSuccess?.(result);
      closeForm();
    } catch (error) {
      console.error('Failed to save appointment:', error);
    }
  };

  // Parse RRULE to RecurrenceRule (simplified)
  const parseRecurrenceRule = (rrule: string) => {
    // This is a simplified parser - a full implementation would use rrule library
    const frequency = rrule.includes('DAILY')
      ? RecurrenceFrequency.DAILY
      : rrule.includes('WEEKLY')
      ? RecurrenceFrequency.WEEKLY
      : rrule.includes('MONTHLY')
      ? RecurrenceFrequency.MONTHLY
      : RecurrenceFrequency.YEARLY;

    return {
      frequency,
      interval: 1,
    };
  };

  // Add checklist item
  const addChecklistItem = () => {
    if (newChecklistItem.trim()) {
      setPrepChecklist([
        ...prepChecklist,
        { id: crypto.randomUUID(), item: newChecklistItem.trim(), completed: false },
      ]);
      setNewChecklistItem('');
    }
  };

  // Remove checklist item
  const removeChecklistItem = (id: string) => {
    setPrepChecklist(prepChecklist.filter((item) => item.id !== id));
  };

  // Add question
  const addQuestion = () => {
    if (newQuestion.trim()) {
      setQuestions([
        ...questions,
        { id: crypto.randomUUID(), question: newQuestion.trim() },
      ]);
      setNewQuestion('');
    }
  };

  // Remove question
  const removeQuestion = (id: string) => {
    setQuestions(questions.filter((q) => q.id !== id));
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b border-warm-gray/20">
        <h2 className="text-xl font-display font-semibold text-navy">
          {isEditing ? 'Edit Appointment' : 'New Appointment'}
        </h2>
        <button
          type="button"
          onClick={onCancel || closeForm}
          className="p-2 hover:bg-cream rounded-lg transition-colors"
        >
          <X className="w-5 h-5 text-warm-gray" />
        </button>
      </div>

      {/* Conflict Warning */}
      {conflictData?.hasConflicts && (
        <ConflictWarning conflicts={conflictData.conflicts} />
      )}

      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-navy mb-1.5">
          Title <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          {...register('title')}
          placeholder="e.g., PT Session with Dr. Smith"
          className={cn(
            'w-full px-4 py-2.5 bg-cream border-2 border-transparent rounded-lg',
            'focus:outline-none focus:border-sage focus:ring-2 focus:ring-sage/20',
            'placeholder:text-warm-gray',
            errors.title && 'border-red-500'
          )}
        />
        {errors.title && (
          <p className="mt-1 text-sm text-red-500">{errors.title.message}</p>
        )}
      </div>

      {/* Type & Child Row */}
      <div className="grid grid-cols-2 gap-4">
        {/* Appointment Type */}
        <div>
          <label className="block text-sm font-medium text-navy mb-1.5">
            Type
          </label>
          <Controller
            name="appointmentType"
            control={control}
            render={({ field }) => (
              <select
                {...field}
                className="w-full px-4 py-2.5 bg-cream border-2 border-transparent rounded-lg focus:outline-none focus:border-sage"
              >
                {Object.entries(APPOINTMENT_TYPE_CONFIG).map(([key, config]) => (
                  <option key={key} value={key}>
                    {config.label}
                  </option>
                ))}
              </select>
            )}
          />
        </div>

        {/* Child */}
        <div>
          <label className="block text-sm font-medium text-navy mb-1.5">
            Child
          </label>
          <select
            {...register('childId')}
            className="w-full px-4 py-2.5 bg-cream border-2 border-transparent rounded-lg focus:outline-none focus:border-sage"
          >
            <option value="">All Children</option>
            {children.map((child) => (
              <option key={child.id} value={child.id}>
                {child.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Date & Time */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium text-navy">
            Date & Time
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              {...register('isAllDay')}
              className="rounded border-warm-gray text-sage focus:ring-sage"
            />
            All Day
          </label>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-warm-gray mb-1">Start</label>
            <Controller
              name="startsAt"
              control={control}
              render={({ field }) => (
                <input
                  type={watchIsAllDay ? 'date' : 'datetime-local'}
                  value={
                    watchIsAllDay
                      ? format(new Date(field.value), 'yyyy-MM-dd')
                      : format(new Date(field.value), "yyyy-MM-dd'T'HH:mm")
                  }
                  onChange={(e) => {
                    const date = watchIsAllDay
                      ? new Date(e.target.value + 'T00:00:00')
                      : new Date(e.target.value);
                    field.onChange(date.toISOString());
                  }}
                  className="w-full px-4 py-2.5 bg-cream border-2 border-transparent rounded-lg focus:outline-none focus:border-sage"
                />
              )}
            />
          </div>
          <div>
            <label className="block text-xs text-warm-gray mb-1">End</label>
            <Controller
              name="endsAt"
              control={control}
              render={({ field }) => (
                <input
                  type={watchIsAllDay ? 'date' : 'datetime-local'}
                  value={
                    watchIsAllDay
                      ? format(new Date(field.value), 'yyyy-MM-dd')
                      : format(new Date(field.value), "yyyy-MM-dd'T'HH:mm")
                  }
                  onChange={(e) => {
                    const date = watchIsAllDay
                      ? new Date(e.target.value + 'T23:59:59')
                      : new Date(e.target.value);
                    field.onChange(date.toISOString());
                  }}
                  className="w-full px-4 py-2.5 bg-cream border-2 border-transparent rounded-lg focus:outline-none focus:border-sage"
                />
              )}
            />
          </div>
        </div>
      </div>

      {/* Recurrence */}
      <div className="space-y-3">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            {...register('isRecurring')}
            className="rounded border-warm-gray text-sage focus:ring-sage"
          />
          <span className="text-sm font-medium text-navy flex items-center gap-1.5">
            <Repeat className="w-4 h-4" />
            Recurring Appointment
          </span>
        </label>

        {watchIsRecurring && (
          <RecurrenceEditor
            value={recurrenceRule}
            onChange={setRecurrenceRule}
            startDate={new Date(watchStartsAt)}
          />
        )}
      </div>

      {/* Location */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-navy">Location</label>
        
        {/* Location Type Tabs */}
        <div className="flex gap-2">
          {Object.entries(LOCATION_TYPE_CONFIG).map(([key, config]) => (
            <Controller
              key={key}
              name="locationType"
              control={control}
              render={({ field }) => (
                <button
                  type="button"
                  onClick={() => field.onChange(key)}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                    field.value === key
                      ? 'bg-sage text-white'
                      : 'bg-cream text-navy hover:bg-cream-dark'
                  )}
                >
                  {key === LocationType.IN_PERSON && <MapPin className="w-4 h-4" />}
                  {key === LocationType.TELEHEALTH && <Video className="w-4 h-4" />}
                  {config.label}
                </button>
              )}
            />
          ))}
        </div>

        {/* Location Details */}
        {watchLocationType === LocationType.IN_PERSON && (
          <div className="space-y-3">
            <input
              type="text"
              {...register('locationName')}
              placeholder="Location name (e.g., Children's Hospital)"
              className="w-full px-4 py-2.5 bg-cream border-2 border-transparent rounded-lg focus:outline-none focus:border-sage"
            />
            <textarea
              {...register('locationAddress')}
              placeholder="Address"
              rows={2}
              className="w-full px-4 py-2.5 bg-cream border-2 border-transparent rounded-lg focus:outline-none focus:border-sage resize-none"
            />
          </div>
        )}

        {watchLocationType === LocationType.TELEHEALTH && (
          <input
            type="url"
            {...register('telehealthLink')}
            placeholder="Video call link (Zoom, Teams, etc.)"
            className="w-full px-4 py-2.5 bg-cream border-2 border-transparent rounded-lg focus:outline-none focus:border-sage"
          />
        )}
      </div>

      {/* Provider */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-navy mb-1.5">
            Provider Name
          </label>
          <input
            type="text"
            {...register('providerName')}
            placeholder="Dr. Sarah Smith"
            className="w-full px-4 py-2.5 bg-cream border-2 border-transparent rounded-lg focus:outline-none focus:border-sage"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-navy mb-1.5">
            Link to Care Team
          </label>
          <select
            {...register('providerId')}
            className="w-full px-4 py-2.5 bg-cream border-2 border-transparent rounded-lg focus:outline-none focus:border-sage"
          >
            <option value="">Select provider...</option>
            {careTeam.map((member) => (
              <option key={member.id} value={member.id}>
                {member.name} ({member.type})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Reminders */}
      <div>
        <label className="block text-sm font-medium text-navy mb-1.5 flex items-center gap-1.5">
          <Bell className="w-4 h-4" />
          Reminders
        </label>
        <Controller
          name="reminderMinutes"
          control={control}
          render={({ field }) => (
            <ReminderSettings
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </div>

      {/* Prep Checklist */}
      <div>
        <label className="block text-sm font-medium text-navy mb-1.5 flex items-center gap-1.5">
          <FileText className="w-4 h-4" />
          Prep Checklist
        </label>
        <div className="space-y-2">
          {prepChecklist.map((item) => (
            <div
              key={item.id}
              className="flex items-center gap-2 px-3 py-2 bg-cream rounded-lg"
            >
              <span className="flex-1 text-sm">{item.item}</span>
              <button
                type="button"
                onClick={() => removeChecklistItem(item.id)}
                className="p-1 text-red-500 hover:bg-red-50 rounded"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          <div className="flex gap-2">
            <input
              type="text"
              value={newChecklistItem}
              onChange={(e) => setNewChecklistItem(e.target.value)}
              placeholder="Add checklist item..."
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addChecklistItem())}
              className="flex-1 px-4 py-2 bg-cream border-2 border-transparent rounded-lg focus:outline-none focus:border-sage text-sm"
            />
            <button
              type="button"
              onClick={addChecklistItem}
              className="px-3 py-2 bg-sage/10 text-sage hover:bg-sage/20 rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Questions to Ask */}
      <div>
        <label className="block text-sm font-medium text-navy mb-1.5 flex items-center gap-1.5">
          <HelpCircle className="w-4 h-4" />
          Questions to Ask
        </label>
        <div className="space-y-2">
          {questions.map((q) => (
            <div
              key={q.id}
              className="flex items-center gap-2 px-3 py-2 bg-cream rounded-lg"
            >
              <span className="flex-1 text-sm">{q.question}</span>
              <button
                type="button"
                onClick={() => removeQuestion(q.id)}
                className="p-1 text-red-500 hover:bg-red-50 rounded"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          <div className="flex gap-2">
            <input
              type="text"
              value={newQuestion}
              onChange={(e) => setNewQuestion(e.target.value)}
              placeholder="Add question..."
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addQuestion())}
              className="flex-1 px-4 py-2 bg-cream border-2 border-transparent rounded-lg focus:outline-none focus:border-sage text-sm"
            />
            <button
              type="button"
              onClick={addQuestion}
              className="px-3 py-2 bg-sage/10 text-sage hover:bg-sage/20 rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Notes */}
      <div>
        <label className="block text-sm font-medium text-navy mb-1.5">
          Notes
        </label>
        <textarea
          {...register('notes')}
          placeholder="Additional notes..."
          rows={3}
          className="w-full px-4 py-2.5 bg-cream border-2 border-transparent rounded-lg focus:outline-none focus:border-sage resize-none"
        />
      </div>

      {/* Color */}
      <div>
        <label className="block text-sm font-medium text-navy mb-1.5">
          Color
        </label>
        <Controller
          name="color"
          control={control}
          render={({ field }) => (
            <div className="flex gap-2">
              {APPOINTMENT_COLORS.map((color) => (
                <button
                  key={color.value}
                  type="button"
                  onClick={() => field.onChange(color.value)}
                  className={cn(
                    'w-8 h-8 rounded-full transition-all',
                    field.value === color.value
                      ? 'ring-2 ring-offset-2 ring-sage scale-110'
                      : 'hover:scale-105'
                  )}
                  style={{ backgroundColor: color.value }}
                  title={color.name}
                />
              ))}
            </div>
          )}
        />
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-4 border-t border-warm-gray/20">
        <button
          type="button"
          onClick={onCancel || closeForm}
          className="flex-1 px-4 py-2.5 bg-cream text-navy font-medium rounded-lg hover:bg-cream-dark transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className={cn(
            'flex-1 px-4 py-2.5 bg-sage text-white font-medium rounded-lg transition-colors',
            'hover:bg-sage-dark disabled:opacity-50 disabled:cursor-not-allowed'
          )}
        >
          {isSubmitting ? 'Saving...' : isEditing ? 'Update Appointment' : 'Create Appointment'}
        </button>
      </div>
    </form>
  );
}


export default AppointmentForm;
